import type {
  AnnouncementBlockAlignChoice,
  AnnouncementBlockCtaStyleChoice,
  AnnouncementBlockSpacingChoice,
  AnnouncementCardStyleChoice,
  AnnouncementHeadingSizeChoice,
  AnnouncementMediaAlignChoice,
  AnnouncementMediaWidthChoice,
  AnnouncementParagraphStyleChoice,
  AnnouncementRowBlockSchema,
} from '@gto/api-client/admin/react-query'

/**
 * The caps the contract enforces, restated so the editor can report them BEFORE
 * a save rather than surfacing a 422 naming a field buried three blocks down.
 *
 * Ground truth is `components.schemas.Announcement*BlockSchema` in
 * `libs/api-client/openapi-admin-schema.json`. A number that drifts from it turns
 * into either an error the operator cannot explain or a save the BE rejects, so
 * re-read the schema rather than the renderer when changing one.
 */
export const HEADING_TEXT_MAX_LENGTH = 200
export const HEADING_HIGHLIGHT_MAX_LENGTH = 200
export const PARAGRAPH_TEXT_MAX_LENGTH = 2000
export const PARAGRAPH_HIGHLIGHT_MAX_LENGTH = 2000

export const BULLET_ITEM_MAX_LENGTH = 300
export const BULLET_ITEMS_MIN = 1
export const BULLET_ITEMS_MAX = 20
export const MEDIA_URL_MAX_LENGTH = 500
export const CTA_BLOCK_TEXT_MAX_LENGTH = 100
export const CTA_BLOCK_LINK_MAX_LENGTH = 500
export const CARD_HEADING_MAX_LENGTH = 100
export const CARD_TEXT_MAX_LENGTH = 500

/** The contract accepts 2 or 3 children per row. */
export const ROW_CHILDREN_MIN = 2
export const ROW_CHILDREN_MAX = 3

/**
 * Identity for one draft block, and the ONLY field that never reaches the server.
 *
 * Array position cannot serve as the key: a drag reorders the array, and React
 * would then reconcile the moved block's inputs onto whatever now sits at that
 * index — which reads as the text jumping between blocks mid-edit. dnd-kit needs
 * the same stable id for its sortable items.
 */
export type BlockDraftId = string

type Aligned = { align: AnnouncementBlockAlignChoice }

/**
 * The gap above the block when it sits at the top level. Optional on the draft
 * like the row's layout fields: a draft built before the field existed reads as
 * the contract default, and `toBlocksPayload` always sends a value.
 */
type Spaced = { spacing?: AnnouncementBlockSpacingChoice }

export type HeadingBlockDraft = Aligned &
  Spaced & {
    id: BlockDraftId
    type: 'heading'
    text: string
    highlight: string
    size?: AnnouncementHeadingSizeChoice
  }

export type ParagraphBlockDraft = Aligned &
  Spaced & {
    id: BlockDraftId
    type: 'paragraph'
    text: string
    /** A phrase painted brighter than the copy around it; empty = none. */
    highlight?: string
    style?: AnnouncementParagraphStyleChoice
  }

export type BulletsBlockDraft = Aligned &
  Spaced & {
    id: BlockDraftId
    type: 'bullets'
    /**
     * One bullet per line, as raw textarea text — the same convention the flat
     * `body` and the route-prefix lists already use. Held as text rather than as
     * the `string[]` the contract wants so that typing a trailing newline, or a
     * blank line between two bullets, does not rewrite the operator's cursor
     * position; `toBlocksPayload` is where it becomes an array.
     */
    itemsText: string
  }

/**
 * What one media block points at — exactly one asset, reached one of three ways.
 *
 * `file` is a pick whose bytes have not left the browser yet; it becomes an
 * `uploadId` when the save pushes it to the bucket. `uploadId` names an object
 * already in our own bucket and is the only one of the three that PERSISTS.
 *
 * `url` is an asset hosted somewhere else. New blocks are authored by upload, but
 * the contract still accepts a link and stored blocks still carry them, so the
 * editor has to keep one editable.
 *
 * The last two are for the SCREEN and are never sent. `storedUrl` is the signed
 * GET the API minted for this page load so the operator can see what is attached;
 * it is deliberately not `url`, because a signed URL looks identical and sending
 * it back would store one that 403s within the hour — the whole bug this shape
 * exists to prevent. `stagedUrl` is the object URL for `file`, which is what lets
 * the preview draw a picture that has not been uploaded yet.
 *
 * Both are kept, rather than one "current preview": a pick that gets REFUSED for
 * being oversized must leave the attached asset's thumbnail exactly where it was.
 */
export type MediaBlockSource = {
  url: string
  uploadId: string
  storedUrl: string
  stagedUrl: string
  file: File | null
}

/**
 * How the asset sits in the space it is given, which is a separate question from
 * which asset it is.
 *
 * `align` shares its name with the text blocks' own `align` and not its values:
 * a media block can sit right, and a heading cannot. The two never mix, because
 * the draft is a discriminated union and every reader switches on `type` first.
 */
export type MediaBlockLayout = {
  align: AnnouncementMediaAlignChoice
  width: AnnouncementMediaWidthChoice
}

export type ImageBlockDraft = MediaBlockSource &
  MediaBlockLayout &
  Spaced & {
    id: BlockDraftId
    type: 'image'
  }

export type VideoBlockDraft = MediaBlockSource &
  MediaBlockLayout &
  Spaced & {
    id: BlockDraftId
    type: 'video'
  }

/**
 * Artwork painted edge to edge across the dialog. No layout: it always fills
 * the width, which is also why it is the one leaf a row cannot hold.
 */
export type HeroBlockDraft = MediaBlockSource &
  Spaced & {
    id: BlockDraftId
    type: 'hero'
  }

/** The block types whose content is an asset rather than text. */
export type MediaBlockDraft = ImageBlockDraft | VideoBlockDraft | HeroBlockDraft

export const MEDIA_BLOCK_KINDS: readonly MediaBlockDraft['type'][] = ['image', 'video', 'hero']

/**
 * Whether the block names an asset at all, by any of the three routes.
 *
 * Shared by validation and by the dirty check so the editor cannot disagree with
 * itself about whether an image block is finished.
 */
export const hasMediaSource = (draft: MediaBlockDraft): boolean =>
  draft.file !== null || draft.uploadId !== '' || draft.url.trim() !== ''

/**
 * What to point an `img` or `video` at for this block right now.
 *
 * A picked file wins: it is the change the operator is looking at the preview to
 * judge, and it has not reached the bucket yet.
 */
export const mediaPreviewUrl = (draft: MediaBlockDraft): string =>
  draft.stagedUrl || draft.storedUrl || draft.url.trim()

export type CtaBlockDraft = Spaced & {
  id: BlockDraftId
  type: 'cta'
  text: string
  link: string
  style: AnnouncementBlockCtaStyleChoice
}

export type CardBlockDraft = Spaced & {
  id: BlockDraftId
  type: 'card'
  heading: string
  text: string
  style?: AnnouncementCardStyleChoice
}

/**
 * Everything a row may hold — which is every block type EXCEPT `row` and `hero`.
 *
 * One level of nesting is enforced here, by typing, exactly as the contract
 * enforces it: `AnnouncementRowBlockSchemaChildrenItem` is a union over the seven
 * leaves with `row` and `hero` absent. A row inside a row does not compile and
 * would 422 if it somehow did, so no runtime depth check has to be written or
 * remembered; a hero in a row is refused the same way.
 */
export type RowLeafBlockDraft =
  | HeadingBlockDraft
  | ParagraphBlockDraft
  | BulletsBlockDraft
  | ImageBlockDraft
  | VideoBlockDraft
  | CtaBlockDraft
  | CardBlockDraft

export type LeafBlockDraft = RowLeafBlockDraft | HeroBlockDraft

export type EmptyBlockDraft = {
  id: BlockDraftId
  type: 'empty'
}

export type RowChildBlockDraft = RowLeafBlockDraft | EmptyBlockDraft

export type RowBlockDraft = Omit<AnnouncementRowBlockSchema, 'children'> & {
  id: BlockDraftId
  type: 'row'
  children: RowChildBlockDraft[]
}

export type BlockDraft = LeafBlockDraft | RowBlockDraft

export type LeafBlockKind = LeafBlockDraft['type']
export type BlockKind = BlockDraft['type']
export type RowChildKind = RowChildBlockDraft['type']

/**
 * What a row's column may be, in the order the "Add block" menu offers them —
 * roughly the order a promo is written in: the headline, its copy, then the
 * things that decorate or close it.
 */
const ROW_LEAF_BLOCK_KINDS: readonly RowLeafBlockDraft['type'][] = [
  'heading',
  'paragraph',
  'bullets',
  'image',
  'video',
  'cta',
  'card',
]

/**
 * Every leaf the top level offers. The hero comes first because it opens the
 * modal; `row` is appended by the editor because it is a container rather than
 * content.
 */
export const LEAF_BLOCK_KINDS: readonly LeafBlockKind[] = ['hero', ...ROW_LEAF_BLOCK_KINDS]

/**
 * What a row's column may be, which is every row leaf plus the deliberate blank.
 * `empty` is offered only here: at the top level a blank block would render
 * nothing and reserve nothing, whereas inside a row it holds a column open so
 * the blocks beside it keep their width.
 */
export const ROW_CHILD_KINDS: readonly RowChildKind[] = [...ROW_LEAF_BLOCK_KINDS, 'empty']

export const BLOCK_KIND_LABELS: Record<BlockKind | RowChildKind, string> = {
  hero: 'Hero image',
  heading: 'Heading',
  paragraph: 'Paragraph',
  bullets: 'Bullets',
  image: 'Image',
  video: 'Video',
  cta: 'Button',
  card: 'Card',
  row: 'Row',
  empty: 'Empty column',
}

/**
 * One line per type, shown in the add menu. Says what the block DOES on the
 * customer's screen, because the type name alone does not distinguish a `cta`
 * from a `card` for someone authoring their first modal.
 */
export const BLOCK_KIND_DESCRIPTIONS: Record<BlockKind | RowChildKind, string> = {
  hero: 'Artwork painted edge to edge, above the copy — put it first to open the modal.',
  heading: 'A headline, optionally with one phrase in the accent colour.',
  paragraph: 'A block of copy, in one of three looks.',

  bullets: 'A chevron list, one item per line.',
  image: 'An uploaded image, full width of the modal.',
  video: 'An uploaded video, full width of the modal.',
  cta: 'A button or an inline text link.',
  card: 'A bordered panel with its own heading — the tier-card shape, plain or tinted.',

  row: 'Two or three blocks with adjustable spacing, alignment, and mobile layout.',
  empty: 'Holds a column open without rendering anything, so its neighbours keep their width.',
}
